// src/components/assessment/RoadSignTest.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { 
    Triangle, Circle, Square, Hexagon, Octagon, 
    Play, Timer, AlertCircle, CheckCircle2, XCircle
} from 'lucide-react';

const SHAPES = [
    { id: 'triangle', icon: Triangle },
    { id: 'circle', icon: Circle },
    { id: 'square', icon: Square },
    { id: 'hexagon', icon: Hexagon },
    { id: 'octagon', icon: Octagon }
];

const COLORS = [
    { id: 'red', class: 'text-red-500 fill-red-500/20' },
    { id: 'blue', class: 'text-blue-500 fill-blue-500/20' },
    { id: 'green', class: 'text-green-500 fill-green-500/20' },
    { id: 'yellow', class: 'text-yellow-500 fill-yellow-500/20' }
];

const RoadSignTest = ({ onComplete }) => {
    const [gameState, setGameState] = useState('intro'); // intro, playing, complete
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(45); // 45 seconds to test fatigue
    const [target, setTarget] = useState(null);
    const [grid, setGrid] = useState([]);
    const [feedback, setFeedback] = useState(null); // 'correct', 'miss'

    // --- GAME ENGINE ---
    const generateLevel = useCallback(() => {
        // 1. Pick a Target
        const targetShape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
        const targetColor = COLORS[Math.floor(Math.random() * COLORS.length)];
        const newTarget = { shape: targetShape, color: targetColor };
        setTarget(newTarget);

        // 2. Generate Grid (4x3 for Mobile-First)
        const gridSize = 12;
        const targetIndex = Math.floor(Math.random() * gridSize);
        const newGrid = [];

        for (let i = 0; i < gridSize; i++) {
            if (i === targetIndex) {
                newGrid.push({ ...newTarget, isTarget: true, id: `item-${i}` });
            } else {
                // Generate Distractor (Ensure it's not identical to target)
                let distShape, distColor;
                do {
                    distShape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
                    distColor = COLORS[Math.floor(Math.random() * COLORS.length)];
                } while (distShape.id === targetShape.id && distColor.id === targetColor.id);
                
                newGrid.push({ shape: distShape, color: distColor, isTarget: false, id: `item-${i}` });
            }
        }
        setGrid(newGrid);
    }, []);

    // --- TIMER LOGIC ---
    useEffect(() => {
        if (gameState === 'playing' && timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
            return () => clearTimeout(timer);
        } else if (timeLeft === 0 && gameState === 'playing') {
            setGameState('complete');
            
            // UPDATED SCORING LOGIC
            const finalScore = Math.min(100, Math.max(0, score * 3));
            
            onComplete({ score: finalScore, raw_hits: score });
        }
    }, [gameState, timeLeft, onComplete, score]);

    // --- INTERACTION ---
    const handleItemClick = (item) => {
        if (item.isTarget) {
            // Correct
            setScore(s => s + 1);
            setFeedback('correct');
            setTimeout(() => {
                setFeedback(null);
                generateLevel();
            }, 200);
        } else {
            // Miss (Penalty)
            setScore(s => Math.max(0, s - 1)); // Prevent negative score
            setFeedback('miss');
            setTimeout(() => setFeedback(null), 200);
        }
    };

    // --- RENDER ---
    if (gameState === 'intro') {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center p-6 animate-in fade-in">
                <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                    <AlertCircle size={40} />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-2">Cognitive Focus Test</h2>
                <p className="text-slate-500 mb-8 max-w-xs text-sm">
                    Tap the target sign as fast as you can. Avoid the distractors.
                </p>
                <button 
                    onClick={() => { setGameState('playing'); generateLevel(); }}
                    className="px-8 py-4 bg-slate-900 text-white rounded-xl font-bold hover:scale-105 transition-transform shadow-xl flex items-center gap-2"
                >
                    <Play size={20} className="fill-white" /> Start Test
                </button>
            </div>
        );
    }

    if (gameState === 'complete') {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center animate-in zoom-in">
                <h2 className="text-3xl font-black text-slate-800 mb-2">Test Complete!</h2>
                {/* B1 fix: MUST match the recorded formula below (score * 3) —
                    ×3 is what history in latestStats/BigQuery holds, so the
                    display conforms to the record, not the other way around. */}
                <div className="text-6xl font-black text-brand-primary mb-2">{Math.min(100, Math.max(0, score * 3))}</div>
                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Focus Score</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full p-4 max-w-lg mx-auto">
            {/* HUD */}
            <div className="flex justify-between items-center mb-6 bg-slate-100 p-3 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 uppercase">Target:</span>
                    <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-lg border border-slate-200 shadow-sm">
                        {target && React.createElement(target.shape.icon, { 
                            className: `w-5 h-5 ${target.color.class}` 
                        })}
                        <span className="text-xs font-bold text-slate-700 capitalize">
                            {target?.color.id} {target?.shape.id}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-slate-700 font-mono font-bold">
                    <Timer size={16} /> {timeLeft}s
                </div>
            </div>

            {/* GRID */}
            <div className="grid grid-cols-4 gap-3 md:gap-4 flex-1 content-center relative">
                {/* Feedback Overlay */}
                {feedback && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
                        {feedback === 'correct' ? 
                            <CheckCircle2 size={80} className="text-green-500 fill-white drop-shadow-2xl animate-in zoom-in duration-150" /> : 
                            <XCircle size={80} className="text-red-500 fill-white drop-shadow-2xl animate-in shake duration-150" />
                        }
                    </div>
                )}

                {grid.map((item) => (
                    <button
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className="aspect-square bg-white rounded-xl border-2 border-slate-100 shadow-sm flex items-center justify-center active:scale-90 transition-transform hover:border-blue-100"
                    >
                        {React.createElement(item.shape.icon, { 
                            className: `w-8 h-8 md:w-10 md:h-10 ${item.color.class} transition-all` 
                        })}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default RoadSignTest;